"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const centers = [{
  name: "ROBOTO Tân Mai",
  district: "Hoàng Mai, Hà Nội"
}, {
  name: "ROBOTO Xã Đàn",
  district: "Đống Đa, Hà Nội"
}, {
  name: "ROBOTO Tam Trinh",
  district: "Hai Bà Trưng, Hà Nội"
}, {
  name: "ROBOTO Hoàng Quốc Việt",
  district: "Cầu Giấy, Hà Nội"
}];
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-sizes",
  alt: "ROBOTO Mascot celebrating"
};
export default function CentersCta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="centerssummaryandclosingcta" data-nav="light" className="w-full bg-background py-16 md:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Part 1: Centers Summary Block */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="rounded-3xl border border-border bg-card p-6 shadow-sm md:p-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-secondary" />
                <span className="text-xs font-semibold tracking-wider text-secondary uppercase">
                  Mạng Lưới Đào Tạo
                </span>
                <span className="hidden h-px w-12 bg-secondary/40 sm:inline-block" />
              </div>

              <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-card-foreground sm:text-3xl">
                Hệ Thống Cơ Sở
              </h2>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                Không gian thực hành chuẩn công nghệ của ROBOTO tại các quận trung tâm Hà Nội.
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline" className="border-border hover:bg-muted">
                <Link to="/FacilitiesCentersPage" className="group">
                  Xem tất cả cơ sở
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Centers Chip Grid */}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {centers.map((center, i) => <motion.div key={center.name} data-index={i} variants={itemVariants} className="group relative flex min-w-0 items-center gap-3.5 rounded-2xl border border-border/80 bg-background/60 p-4 transition-all hover:border-secondary hover:bg-background hover:shadow-xs">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary/15 text-secondary">
                  <MapPin className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground group-hover:text-primary">
                    {center.name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {center.district}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Part 2: Closing Full-Width CTA Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45,
        ease: "easeOut",
        delay: shouldReduceMotion ? 0 : 0.1
      }} className="relative mt-12 overflow-hidden rounded-3xl bg-primary px-6 py-12 text-primary-foreground shadow-xl md:px-12 md:py-16">
          {/* Subtle Technical Blueprint Coordinates Overlay */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,_var(--color-primary-foreground)_1px,_transparent_1px)] bg-[size:24px_24px]" />
          <div aria-hidden="true" className="pointer-events-none absolute -right-16 -bottom-16 size-80 rounded-full bg-accent/20 blur-3xl" />

          {/* Static Decorative Floating Mascot */}
          <div aria-hidden="true" className="pointer-events-none select-none absolute bottom-3 right-3 sm:bottom-4 sm:right-6 md:bottom-6 md:right-8 lg:bottom-8 lg:right-10 z-0 hidden sm:block">
            <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} className="h-20 w-20 md:h-28 md:w-28 lg:h-32 lg:w-32 bg-transparent object-contain drop-shadow-md select-none pointer-events-none" loading="lazy" />
          </div>

          <div className="relative z-10 mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-semibold tracking-wide text-primary-foreground">
              <Sparkles className="size-3.5 text-accent" />
              <span>Khởi đầu hành trình sáng tạo công nghệ</span>
            </div>

            <h3 className="mt-4 font-serif text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl md:text-5xl">
              BẠN CHƯA BIẾT CON NÊN BẮT ĐẦU TỪ ĐÂU?
            </h3>

            <p className="mx-auto mt-4 max-w-2xl text-base text-primary-foreground/90 sm:text-lg">
              Đội ngũ ROBOTO có thể giúp phụ huynh xác định chương trình phù hợp dựa trên độ tuổi, nền tảng và mục tiêu học tập của học viên.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button asChild size="lg" className="w-full bg-accent text-accent-foreground shadow-md hover:bg-accent/90 sm:w-auto font-semibold px-7">
                <Link to="/FreeTrialContactPage">
                  ĐĂNG KÝ TƯ VẤN
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full border-primary-foreground/40 bg-primary-foreground/10 text-primary-foreground backdrop-blur-xs hover:bg-primary-foreground hover:text-primary sm:w-auto px-7">
                <Link to="/ProgramsPage">
                  XEM CHƯƠNG TRÌNH
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}